import { useEffect, useState, type ReactNode } from "react";
import { AuthContext, type User } from "./auth";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        const [configResp, meResp] = await Promise.all([
          fetch("/api/auth/config", { credentials: "same-origin" }),
          fetch("/api/auth/me", { credentials: "same-origin" }),
        ]);
        if (cancelled) return;
        if (configResp.ok) {
          const { googleClientId: cid } = await configResp.json();
          setGoogleClientId(cid ?? null);
        }
        if (meResp.ok) {
          const { user: u } = await meResp.json();
          setUser(u);
        }
      } catch {
        // offline or server down
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (googleToken: string) => {
    const resp = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ googleToken }),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error ?? "login failed");
    }
    const { user: u } = await resp.json();
    setUser(u);
  };

  const logout = async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, googleClientId, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
